import React, { useState, useEffect } from "react";
import { FaMapMarkerAlt, FaEdit, FaTrash } from "react-icons/fa";
import { getAddresses, removeAddress } from "../api/address.api";
import { getStaffId } from "../utils/storage";
import AddressFormModal from "./AddressFormModal";
import ConfirmModal from "../Popups/ConfirmModal";
import LoadingState from "./States/LoadingState";

const formatAddress = (a) =>
  [a.street, a.city, a.state, a.postalCode].filter(Boolean).join(", ");

const ProfileAddressesSection = () => {
  const staffId = getStaffId();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editAddress, setEditAddress] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    if (!staffId) {
      setLoading(false);
      return;
    }
    getAddresses(staffId)
      .then((res) => setAddresses(res.data?.addresses || []))
      .catch(() => setError("Error fetching addresses"))
      .finally(() => setLoading(false));
  }, [staffId]);

  const openCreate = () => {
    setEditAddress(null);
    setModalOpen(true);
  };

  const onEdit = (address) => {
    setEditAddress(address);
    setModalOpen(true);
  };

  const handleSaved = (addr, action) => {
    if (action === "update") {
      setAddresses((prev) => prev.map((a) => (a._id === addr._id ? addr : a)));
    } else {
      setAddresses((prev) => [...prev, addr]);
    }
    setError("");
  };

  const confirmDelete = () => {
    if (!pendingDeleteId) return;
    removeAddress(staffId, pendingDeleteId)
      .then(() => setAddresses((prev) => prev.filter((a) => a._id !== pendingDeleteId)))
      .catch(() => setError("Error removing address"))
      .finally(() => setPendingDeleteId(null));
  };

  const isDefault = (address, index) =>
    index === 0 || (address.addressType || "").toLowerCase() === "home";

  return (
    <section className="bg-surface-lowest border border-surface-container rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-surface-container">
        <div className="flex items-center gap-2">
          <FaMapMarkerAlt className="text-primary" />
          <h2 className="font-semibold text-on-primary-fixed-var">Addresses</h2>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="text-sm font-medium text-primary hover:text-primary-container"
        >
          + Add Address
        </button>
      </div>

      <div className="px-5 py-3 space-y-3">
        {error && !modalOpen && <p className="text-sm text-red-500">{error}</p>}
        {loading && <LoadingState size="sm" message="Loading…" className="py-6" />}
        {!loading && addresses.length === 0 && (
          <p className="text-sm text-on-surface-variant">No addresses added yet.</p>
        )}
        {addresses.map((a, i) => (
          <div key={a._id} className="flex items-start justify-between gap-2 py-3 border-b border-surface-container last:border-0">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="font-medium text-on-primary-fixed-var">{a.addressType || "Address"}</span>
                {isDefault(a, i) && (
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-primary text-white px-2 py-0.5 rounded">
                    Default
                  </span>
                )}
              </div>
              <p className="text-sm text-on-surface-variant break-words">{formatAddress(a)}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button type="button" onClick={() => onEdit(a)} className="p-2.5 rounded-full text-on-surface-variant hover:text-primary hover:bg-surface-low" aria-label="Edit">
                <FaEdit size={16} />
              </button>
              <button type="button" onClick={() => setPendingDeleteId(a._id)} className="p-2.5 rounded-full text-on-surface-variant hover:text-red-500 hover:bg-red-50" aria-label="Delete">
                <FaTrash size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <AddressFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        staffId={staffId}
        editAddress={editAddress}
        existingAddresses={addresses}
        onSaved={handleSaved}
      />

      <ConfirmModal
        open={!!pendingDeleteId}
        title="Delete this address?"
        message="This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </section>
  );
};

export default ProfileAddressesSection;
